import { isPaidPortalMember } from "./membershipTier";
import { resolveMembershipIdentity } from "./membershipIdentity";

/** Sentinel in AllowedMembershipIds for unpaid Explorers (not a Memberships table row). */
export const EXPLORER_VISIBILITY_ID = -1;

export const EXPLORER_VISIBILITY_LABEL = "Explorers";

/** Comma-separated membership IDs; null/empty = visible to all membership types. */
export function parseAllowedMembershipIds(
  allowedMembershipIds?: string | null,
): number[] {
  if (!allowedMembershipIds?.trim()) return [];
  return allowedMembershipIds
    .split(",")
    .map((part) => parseInt(part.trim(), 10))
    .filter(
      (id) =>
        Number.isFinite(id) && (id > 0 || id === EXPLORER_VISIBILITY_ID),
    );
}

export function isEventOpenToAllMemberships(
  allowedMembershipIds?: string | null,
): boolean {
  return parseAllowedMembershipIds(allowedMembershipIds).length === 0;
}

export function eventAllowsExplorers(
  allowedMembershipIds?: string | null,
): boolean {
  return (
    isEventOpenToAllMemberships(allowedMembershipIds) ||
    parseAllowedMembershipIds(allowedMembershipIds).includes(
      EXPLORER_VISIBILITY_ID,
    )
  );
}

export function serializeAllowedMembershipIds(
  selectedIds: number[],
  allVisibilityIds: number[],
): string | null | undefined {
  const selected = Array.from(
    new Set(
      selectedIds.filter(
        (id) => id > 0 || id === EXPLORER_VISIBILITY_ID,
      ),
    ),
  ).sort((a, b) => a - b);
  if (selected.length === 0) return undefined;
  if (
    allVisibilityIds.length > 0 &&
    selected.length >= allVisibilityIds.length &&
    allVisibilityIds.every((id) => selected.includes(id))
  ) {
    return null;
  }
  return selected.join(",");
}

/** "Youth Membership", "Early Stage Membership", and short forms resolve to the same youth tier key. */
export function normalizeMembershipKey(name: string): string {
  let t = name.trim();
  if (/\s+membership\s*$/i.test(t)) {
    t = t.replace(/\s+membership\s*$/i, "").trim();
  }
  const key = t.toLowerCase();
  if (key === "youth" || key === "early stage") return "youth";
  return key;
}

function findMembershipByUserName(
  memberships: readonly { id: number; name: string }[],
  userMembershipName: string,
): { id: number; name: string } | null {
  const trimmed = userMembershipName.trim();
  const exact = memberships.find((m) => m.name === trimmed);
  if (exact) return exact;
  const lower = trimmed.toLowerCase();
  const ci = memberships.find((m) => m.name.trim().toLowerCase() === lower);
  if (ci) return ci;
  const userKey = normalizeMembershipKey(trimmed);
  const byKey = memberships.filter(
    (m) => normalizeMembershipKey(m.name) === userKey,
  );
  return byKey.length === 1 ? byKey[0] : null;
}

/** Events only: Youth + Early Stage share one visibility tier (matches API). */
function getYouthTierMembershipIds(
  memberships: readonly { id: number; name: string }[],
  userMembershipId?: number | null,
  membershipName?: string | null,
): Set<number> {
  const ids = new Set<number>();
  for (const m of memberships) {
    if (normalizeMembershipKey(m.name) === "youth") ids.add(m.id);
  }
  if (membershipName && normalizeMembershipKey(membershipName) === "youth") {
    if (userMembershipId != null && userMembershipId > 0) {
      ids.add(userMembershipId);
    }
  }
  return ids;
}

function canAccessEventMembership(
  allowedMembershipIds: string | null | undefined,
  membershipId: number,
  youthTierMembershipIds: ReadonlySet<number>,
): boolean {
  const allowed = parseAllowedMembershipIds(allowedMembershipIds);
  if (allowed.includes(membershipId)) return true;
  if (
    youthTierMembershipIds.size > 0 &&
    youthTierMembershipIds.has(membershipId) &&
    allowed.some((id) => youthTierMembershipIds.has(id))
  ) {
    return true;
  }
  return false;
}

/** Requires an active paid membership (hasPaid or Stripe active/trialing), then name/id. */
export function resolveEffectiveMembershipId(
  membershipId?: number | null,
  membershipName?: string | null,
  memberships?: readonly { id: number; name: string }[],
  hasPaid?: number | null,
  stripeStatus?: string | null,
): number | null {
  if (!isPaidPortalMember({ hasPaid, stripeStatus })) return null;
  const name = membershipName?.trim();
  if (name && memberships?.length) {
    const match = findMembershipByUserName(memberships, name);
    if (match) return match.id;
  }
  if (membershipId != null && membershipId > 0) return membershipId;
  return null;
}

/** True while membership list is loading and name-based access cannot be evaluated yet. */
export function isMembershipAccessCheckPending(
  allowedMembershipIds: string | null | undefined,
  membershipName?: string | null,
  memberships?: readonly { id: number; name: string }[],
  hasPaid?: number | null,
  stripeStatus?: string | null,
): boolean {
  if (isEventOpenToAllMemberships(allowedMembershipIds)) return false;
  if (!isPaidPortalMember({ hasPaid, stripeStatus })) return false;
  if (!membershipName?.trim()) return false;
  return !memberships?.length;
}

export function canUserViewEvent(
  allowedMembershipIds: string | null | undefined,
  userMembershipId?: number | null,
  options?: {
    membershipName?: string | null;
    memberships?: readonly { id: number; name: string }[];
    hasPaid?: number | null;
    stripeStatus?: string | null;
  },
): boolean {
  if (isEventOpenToAllMemberships(allowedMembershipIds)) return true;

  // Unpaid Explorers when admin included Explorers in visibility.
  if (
    resolveMembershipIdentity({
      hasPaid: options?.hasPaid ?? null,
      stripeStatus: options?.stripeStatus ?? null,
      membershipName: options?.membershipName ?? null,
    }).isExplorer
  ) {
    return eventAllowsExplorers(allowedMembershipIds);
  }

  const effectiveId = resolveEffectiveMembershipId(
    userMembershipId,
    options?.membershipName,
    options?.memberships,
    options?.hasPaid,
    options?.stripeStatus,
  );
  if (!effectiveId || effectiveId <= 0) return false;
  const youthTierIds = getYouthTierMembershipIds(
    options?.memberships ?? [],
    userMembershipId,
    options?.membershipName,
  );
  return canAccessEventMembership(
    allowedMembershipIds,
    effectiveId,
    youthTierIds,
  );
}

export function defaultSelectedMembershipIds(
  memberships: { id: number }[],
  allowedMembershipIds?: string | null,
): number[] {
  const parsed = parseAllowedMembershipIds(allowedMembershipIds);
  if (parsed.length > 0) return parsed;
  return memberships.map((m) => m.id);
}
